import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { BLOG_ARTICLES } from "./blog/articles";
import { ArrowLeft, ArrowUpRight, Clock, Calendar } from "lucide-react";

const COPY = {
  de: { back: "Zurück zur Übersicht", readTime: "Min.", cta: "Termin buchen" },
  en: { back: "Back to overview", readTime: "min", cta: "Book appointment" },
};

/**
 * Renders a lightweight subset of markdown-ish paragraphs (## headings,
 * - bullet lists, [text](url) links, **bold**). Keeps the codebase dependency-free.
 */
function renderParagraph(text, key) {
  if (text.startsWith("## ")) {
    return (
      <h2 key={key} className="font-display text-3xl text-ink mt-14 mb-4 leading-tight">
        {text.substring(3)}
      </h2>
    );
  }
  if (text.startsWith("- ")) {
    return (
      <li key={key} className="text-ink-muted leading-relaxed pl-1">
        {renderInline(text.substring(2))}
      </li>
    );
  }
  return (
    <p key={key} className="text-ink-muted leading-relaxed mb-5">
      {renderInline(text)}
    </p>
  );
}

function renderInline(text) {
  // Handle [label](url) and **bold** minimally.
  const nodes = [];
  let rest = text;
  let i = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
  const boldRe = /\*\*([^*]+)\*\*/;

  while (rest.length > 0) {
    const l = rest.match(linkRe);
    const b = rest.match(boldRe);
    const lIdx = l ? l.index : Infinity;
    const bIdx = b ? b.index : Infinity;

    if (lIdx === Infinity && bIdx === Infinity) {
      nodes.push(<React.Fragment key={i++}>{rest}</React.Fragment>);
      break;
    }
    if (lIdx < bIdx) {
      nodes.push(<React.Fragment key={i++}>{rest.substring(0, lIdx)}</React.Fragment>);
      const href = l[2];
      const isInternal = href.startsWith("/");
      nodes.push(
        isInternal ? (
          <Link key={i++} to={href} className="text-terracotta underline underline-offset-2 hover:no-underline">
            {l[1]}
          </Link>
        ) : (
          <a key={i++} href={href} className="text-terracotta underline" target="_blank" rel="noopener noreferrer">
            {l[1]}
          </a>
        )
      );
      rest = rest.substring(lIdx + l[0].length);
    } else {
      nodes.push(<React.Fragment key={i++}>{rest.substring(0, bIdx)}</React.Fragment>);
      nodes.push(
        <strong key={i++} className="text-ink font-medium">
          {b[1]}
        </strong>
      );
      rest = rest.substring(bIdx + b[0].length);
    }
  }
  return nodes;
}

/**
 * Group consecutive bullet lines into a single <ul>.
 */
function renderBody(body) {
  const out = [];
  let listBuffer = [];
  const flush = () => {
    if (listBuffer.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 mb-6 space-y-2">
          {listBuffer}
        </ul>
      );
      listBuffer = [];
    }
  };
  body.forEach((line, idx) => {
    if (line.startsWith("- ")) {
      listBuffer.push(renderParagraph(line, idx));
    } else {
      flush();
      out.push(renderParagraph(line, idx));
    }
  });
  flush();
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useLang();
  const article = BLOG_ARTICLES.find((a) => a.slug === slug);
  const c = COPY[lang] || COPY.de;

  useEffect(() => {
    if (article && typeof window !== "undefined" && typeof window.gtag === "function") {
      try {
        window.gtag("event", "view_article", { item_id: article.slug });
      } catch (_) {}
    }
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const t = article[lang] || article.de;

  return (
    <div data-testid="blog-post-page">
      {/* Header image */}
      <section className="relative h-[45vh] min-h-[300px] overflow-hidden">
        <img src={article.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/50 to-background" />
      </section>

      <article className="max-w-3xl mx-auto px-6 lg:px-12 -mt-32 relative z-10">
        <div className="bg-white border border-border rounded-md p-8 lg:p-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-terracotta hover:underline mb-6"
            data-testid="blog-post-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
            {c.back}
          </Link>

          <h1 className="font-display text-4xl lg:text-5xl text-ink leading-[1.1]">{t.title}</h1>

          <div className="mt-5 flex flex-wrap items-center gap-5 text-xs text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
              {new Date(article.date).toLocaleDateString(lang === "en" ? "en-GB" : "de-CH", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              {article.readMin} {c.readTime}
            </span>
          </div>

          <p className="mt-8 text-lg text-ink-muted leading-relaxed italic border-l-2 border-terracotta/50 pl-5">
            {t.excerpt}
          </p>

          <div className="mt-10 prose-lg">{renderBody(t.body)}</div>

          <div className="mt-14 pt-8 border-t border-border">
            <Link
              to="/booking"
              className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors"
              data-testid="blog-post-book-cta"
            >
              {c.cta}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </article>
      <div className="h-24" />
    </div>
  );
}
